import React from "react";
import CsvDownloader from "react-csv-downloader";
import map from "ramda/src/map";
import applySpec from "ramda/src/applySpec";
import compose from "ramda/src/compose";
import DownloadIcon from "@atlaskit/icon/glyph/download";
import { ProgressTracker, Stages } from "@atlaskit/progress-tracker";

import { useModalContext } from "../../contexts/ModalContext/context";
import { Button } from "../../UI/Button";
import { Grid } from "../../UI/Grid";
import { Typography } from "../../UI/Typography";
import { Modal } from "../Modal";
import { pathOrString } from "../../helpers/pathOrString";
import { IPlacementWorksheetUnit } from "../../services/PlacementWorksheetApi";
import { UploadUnitNumberButton } from "../UploadUnitNumberButton";

const generateData = map(
  applySpec<Record<string, string>>({
    "unit id": pathOrString(["unitId"]),
    "unit number": pathOrString(["unitNumber"]),
  })
);

interface IProps {
  filteredData: IPlacementWorksheetUnit[];
  onSuccess: () => Promise<void>;
}

export const UploadUnitNumbersModal: React.FC<IProps> = (props) => {
  const { setIsOpen } = useModalContext();
  const [isDownload, setIsDownload] = React.useState(false);
  const [step, setStep] = React.useState(1);

  const closeModal = () => setIsOpen(false);

  const setStepTwo = () => setStep(2);
  const setStepOne = () => setStep(1);

  const setDownload = () => setIsDownload(true);

  const items: Stages = [
    {
      id: "1",
      label: "Step One",
      percentageComplete: step === 1 ? 0 : 100,
      status: step === 1 ? "current" : "visited",
      onClick: setStepOne,
    },
    {
      id: "2",
      label: "Step Two",
      percentageComplete: 0,
      status: step === 2 ? "current" : isDownload ? "visited" : "disabled",
      onClick: setStepTwo,
    },
  ];

  return (
    <Modal
      maxWidth="sm"
      onClose={closeModal}
      title="Upload List of Unit Numbers"
      actions={
        <>
          <Button appearance="subtle" onClick={closeModal}>
            Cancel
          </Button>
        </>
      }
    >
      <Grid container spacing={2} px={3} pb={3}>
        <Grid item xs={12}>
          <ProgressTracker items={items} />
        </Grid>
        {step === 1 && (
          <>
            <Grid item xs={12}>
              <Typography align="center" gutterBottom>
                Download the guide file and open in Excel.
              </Typography>
            </Grid>
            <Grid item xs={12} display="flex" justifyContent="center">
              <CsvDownloader filename="placement-worksheet-units-ids" datas={generateData(props.filteredData)}>
                <Button
                  appearance="primary"
                  onClick={compose(setDownload, setStepTwo)}
                  iconBefore={<DownloadIcon label="download" />}
                >
                  Download file
                </Button>
              </CsvDownloader>
            </Grid>
          </>
        )}
        {step === 2 && (
          <>
            <Grid item xs={12}>
              <Typography align="center" gutterBottom>
                Enter your unit numbers into Column B, save as a .csv file and upload below.
              </Typography>
            </Grid>
            <Grid item xs={12} display="flex" justifyContent="center">
              <UploadUnitNumberButton onSuccess={props.onSuccess} />
            </Grid>
          </>
        )}
      </Grid>
    </Modal>
  );
};
